import moment from 'moment';
import { getRenderingApiUrl } from 'infrastructure/pdf';
import { calculateAcceptanceDate, generatePdf } from 'framework/markdownEditor';
import { fetchOfferTemplate } from './offerEditor/services/OfferTemplateService';
import { CandidateDocumentData, OfferPdfSourceData } from './types';
import { addIndefiniteArticle } from './offerTemplateUtils';
import { CONTRACT_TYPES } from 'domain/shared/candidate';

const getCandidateField = (candidate: CandidateDocumentData, source: string, fallback?: string) =>
  candidate.getValueBySource?.(source) ?? fallback ?? '';

const createOfferPdfFile = async ({ offer, candidate, acceptanceDate, templateContent }: OfferPdfSourceData) => {
  const content = templateContent ?? (await fetchOfferTemplate(offer))?.content;

  if (!content) {
    throw new Error('Failed to load offer template');
  }

  const compensationDetail = offer.compensationDetails?.[0] ?? {};
  const firstName = getCandidateField(candidate, 'name.firstName', candidate.name?.firstName);
  const fullName = getCandidateField(candidate, 'name.fullName', candidate.name?.fullName);
  const vacancySpecialization =
    candidate.lastAppliedVacancyData?.specialization ??
    getCandidateField(candidate, 'lastAppliedVacancyData.specialization');
  const { contractType, finalAmountGross, probationAmountGross, currency, office } = compensationDetail;
  const viewData = {
    candidateName: firstName,
    vacancySpecialization: addIndefiniteArticle(vacancySpecialization),
    acceptanceDate: acceptanceDate || calculateAcceptanceDate(moment()),
    office,
    compensationDetails: [
      {
        contractType,
        isEC: contractType === CONTRACT_TYPES.EC,
        isB2B: contractType === CONTRACT_TYPES.B2B,
        finalAmountGross,
        probationAmountGross,
        currency,
      },
    ],
    fullName,
  };
  const pdfBlob = await generatePdf(getRenderingApiUrl(), content, viewData);
  const fileName = `job offer ${viewData.fullName}.pdf`;
  const file = new File([pdfBlob], fileName, { type: 'application/pdf' });

  return {
    file,
    fileName,
    pdfBlob,
  };
};

export const uploadOfferPdfFile = async ({
  offer,
  candidate,
  acceptanceDate,
  uploadFile,
  templateContent,
}: OfferPdfSourceData & {
  uploadFile: ({ file }: { file: File }) => Promise<{
    path?: string;
    id?: string;
  }>;
}) => {
  const { file, fileName, pdfBlob } = await createOfferPdfFile({ offer, candidate, acceptanceDate, templateContent });
  const uploadResult = await uploadFile({ file });
  const tempPath = uploadResult?.path || uploadResult?.id;

  if (!tempPath) throw new Error('Failed to upload generated offer document');

  return {
    file,
    fileName,
    pdfBlob,
    tempPath,
  };
};
